import React from 'react';
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Sort, SortSettingsModel } from '@syncfusion/ej2-react-grids';
import { ConnectionTypeModel, DataSourceModel } from '../types';
import { getProviderDisplayName, getSourceObjectDisplay } from '../utilities';

interface DataSourceGridProps {
    sources: DataSourceModel[];
    connectionTypes: ConnectionTypeModel[];
    testingSourceID: string | null;
    onTest: (source: DataSourceModel) => void;
    onEdit: (source: DataSourceModel) => void;
    onDelete: (source: DataSourceModel) => void;
}

const sortSettings: SortSettingsModel = {
    columns: [{ field: 'Name', direction: 'Ascending' }],
    allowUnsort: false,
};

const formatDateOnly = (value?: string | null) => {
    if (!value) {
        return '';
    }

    return new Date(value).toLocaleDateString();
};

const formatFullDate = (value?: string | null) => {
    if (!value) {
        return '';
    }

    return new Date(value).toLocaleString();
};

const getLastTestStatus = (source: DataSourceModel) => {
    if (source.LastTestSuccessful == null) {
        return InovxLoader.t('facility:dataSources.status.notTested');
    }

    return source.LastTestSuccessful
        ? InovxLoader.t('facility:dataSources.status.successful')
        : InovxLoader.t('facility:dataSources.status.failed');
};

const formatLastTestCell = (source: DataSourceModel) => {
    const status = getLastTestStatus(source);
    const date = formatDateOnly(source.LastTestDate);
    return `${status}${date ? ` ${date}` : ''}`;
};

const formatLastTestTooltip = (source: DataSourceModel) => {
    const status = getLastTestStatus(source);
    const date = formatFullDate(source.LastTestDate);
    const message = source.LastTestMessage ? ` - ${source.LastTestMessage}` : '';
    return `${status}${date ? ` ${date}` : ''}${message}`;
};

const DataSourceGrid: React.FC<DataSourceGridProps> = ({
    sources,
    connectionTypes,
    testingSourceID,
    onTest,
    onEdit,
    onDelete,
}) => {
    const renderName = React.useCallback((row: DataSourceModel) => (
        <span className="data-sources-name-cell" title={row.Name}>{row.Name}</span>
    ), []);

    const renderProvider = React.useCallback((row: DataSourceModel) => (
        <span title={getProviderDisplayName(connectionTypes, row.Type, row.TypeName)}>
            {getProviderDisplayName(connectionTypes, row.Type, row.TypeName)}
        </span>
    ), [connectionTypes]);

    const renderSourceObject = React.useCallback((row: DataSourceModel) => {
        const value = getSourceObjectDisplay(row.SourceConfig);
        return <span title={value}>{value}</span>;
    }, []);

    const renderLastTest = React.useCallback((row: DataSourceModel) => {
        const value = formatLastTestCell(row);
        const tooltip = formatLastTestTooltip(row);
        const statusClass = row.LastTestSuccessful == null ? 'neutral' : row.LastTestSuccessful ? 'success' : 'failed';
        return <span className={`data-sources-status-badge ${statusClass}`} title={tooltip}>{value}</span>;
    }, []);

    const renderActions = React.useCallback((row: DataSourceModel) => (
        <div className="kb-action-buttons data-sources-grid-actions">
            <button
                type="button"
                className="e-btn e-flat"
                title={InovxLoader.t('facility:dataSources.testConnection')}
                disabled={testingSourceID === row.ID}
                onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onTest(row);
                }}
            >
                <span className="e-btn-icon e-icons e-refresh"></span>
            </button>
            <button
                type="button"
                className="e-btn e-flat"
                title={InovxLoader.t('facility:dataSources.edit')}
                onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onEdit(row);
                }}
            >
                <span className="e-btn-icon e-icons e-edit"></span>
            </button>
            <button
                type="button"
                className="e-btn e-flat"
                title={InovxLoader.t('facility:dataSources.delete')}
                onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onDelete(row);
                }}
            >
                <span className="e-btn-icon e-icons e-delete"></span>
            </button>
        </div>
    ), [onDelete, onEdit, onTest, testingSourceID]);

    return (
        <GridComponent
            className="data-sources-grid"
            dataSource={sources}
            allowSorting={true}
            sortSettings={sortSettings}
            allowSelection={false}
            locale={typeof InovxLoader !== 'undefined' ? InovxLoader.getSyncfusionLocale() : 'en-US'}
        >
            <ColumnsDirective>
                <ColumnDirective field="Name" headerText={InovxLoader.t('facility:dataSources.columns.name')} width="28%" template={renderName} />
                <ColumnDirective field="TypeName" headerText={InovxLoader.t('facility:dataSources.columns.provider')} width="18%" template={renderProvider} />
                <ColumnDirective headerText={InovxLoader.t('facility:dataSources.columns.sourceObject')} width="24%" allowSorting={false} template={renderSourceObject} />
                <ColumnDirective field="LastTestDate" headerText={InovxLoader.t('facility:dataSources.columns.lastTest')} width="16%" template={renderLastTest} />
                <ColumnDirective headerText="" width="160" allowSorting={false} template={renderActions} />
            </ColumnsDirective>
            <Inject services={[Sort]} />
        </GridComponent>
    );
};

export default DataSourceGrid;
